import { Alert } from '@linktr.ee/arbor/components/Alert';
import { InfoIcon } from '@linktr.ee/arbor/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthForm from '@shared/components/AuthForm';
import AuthLink from '@shared/components/AuthLink';
import Button from '@shared/components/Button';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import {
  getAuthProtectedIdentifierVerificationFailureReasonCode,
  publishAuthProtectedIdentifierVerificationCompletedEvent,
  publishAuthProtectedIdentifierVerificationSubmittedEvent,
  type AuthProtectedIdentifierVerificationFailureReasonCode,
} from '@shared/utils/analytics/events';
import { usePublishScreenViewedEvent } from '@shared/utils/analytics/hooks';
import { validateEmail } from '@shared/utils/validation';
import { translateValidationError } from '@shared/utils/validationMessages';
import bannerLoginImage from '@universal-login/images/banner-login-desktop.webp';
import { ProtectedEmailConfirmationScreenProps } from '@universal-login/types/descope';

const PROTECTED_EMAIL_CONFIRMATION_SCREEN_TITLE = 'Log In - Confirm Email';
const PROTECTED_EMAIL_FAILURE_INVALID_EMAIL: AuthProtectedIdentifierVerificationFailureReasonCode =
  'invalid_email' as AuthProtectedIdentifierVerificationFailureReasonCode;

const ProtectedEmailConfirmationScreen: React.FC<
  ProtectedEmailConfirmationScreenProps
> = ({
  errorText,
  rateLimited = false,
  retryAfterSeconds = 0,
  maskedEmail,
  onClickConfirm,
  onClickUseDifferentLogin,
}) => {
  const { t } = useTranslation();
  usePublishScreenViewedEvent(PROTECTED_EMAIL_CONFIRMATION_SCREEN_TITLE);

  const [email, setEmail] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [hasSubmissionFailed, setHasSubmissionFailed] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    Math.max(0, Math.ceil(retryAfterSeconds))
  );
  const emailInputRef = useRef<HTMLInputElement>(null);
  const pendingSubmissionRef = useRef(false);
  const completionPublishedRef = useRef(false);
  const hasSeenClearedErrorTextForSubmissionRef = useRef(false);

  useEffect(() => {
    return () => {
      if (!pendingSubmissionRef.current || completionPublishedRef.current) {
        return;
      }

      pendingSubmissionRef.current = false;
      completionPublishedRef.current = true;
      publishAuthProtectedIdentifierVerificationCompletedEvent('succeeded');
    };
  }, []);

  useEffect(() => {
    if (!errorText || !isConfirming || completionPublishedRef.current) {
      if (!errorText && isConfirming) {
        hasSeenClearedErrorTextForSubmissionRef.current = true;
      }
      return;
    }

    if (!hasSeenClearedErrorTextForSubmissionRef.current) {
      return;
    }

    pendingSubmissionRef.current = false;
    completionPublishedRef.current = true;
    publishAuthProtectedIdentifierVerificationCompletedEvent(
      'failed',
      getAuthProtectedIdentifierVerificationFailureReasonCode(errorText)
    );
    setIsConfirming(false);
  }, [errorText, isConfirming]);

  useEffect(() => {
    setRemainingSeconds(Math.max(0, Math.ceil(retryAfterSeconds)));
  }, [rateLimited, retryAfterSeconds]);

  useEffect(() => {
    if (!rateLimited || remainingSeconds <= 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRemainingSeconds(currentSeconds => Math.max(0, currentSeconds - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [rateLimited, remainingSeconds]);

  const isCooldownActive = rateLimited && remainingSeconds > 0;

  const handleConfirm = async () => {
    if (isCooldownActive) {
      return;
    }

    completionPublishedRef.current = false;
    publishAuthProtectedIdentifierVerificationSubmittedEvent();
    setValidationError(undefined);
    setHasSubmissionFailed(false);

    const trimmedEmail = email.trim();
    const validation = validateEmail(trimmedEmail);

    if (!validation.isValid) {
      setValidationError(validation.error);
      // The inline error is not a live region, so move focus to the field to
      // get it announced (aria-describedby) when submitting from the button.
      emailInputRef.current?.focus();
      completionPublishedRef.current = true;
      publishAuthProtectedIdentifierVerificationCompletedEvent(
        'failed',
        PROTECTED_EMAIL_FAILURE_INVALID_EMAIL
      );
      return;
    }

    pendingSubmissionRef.current = true;
    hasSeenClearedErrorTextForSubmissionRef.current = !errorText;
    setIsConfirming(true);

    try {
      await onClickConfirm(trimmedEmail);
    } catch (error) {
      pendingSubmissionRef.current = false;
      completionPublishedRef.current = true;
      publishAuthProtectedIdentifierVerificationCompletedEvent(
        'failed',
        getAuthProtectedIdentifierVerificationFailureReasonCode(error)
      );
      setHasSubmissionFailed(true);
      setIsConfirming(false);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValidationError(undefined);
    setHasSubmissionFailed(false);
    setEmail(event.target.value);
  };

  // Validation failures are communicated by the field alone; suppress the
  // banner until the inline error is resolved so the two never stack.
  const showSubmissionErrorBanner =
    !validationError && (hasSubmissionFailed || Boolean(errorText));
  // errorText is unvetted flow-engine text. Only the recognized user-facing
  // families are shown verbatim (the mismatch string classifies as
  // invalid_email — its "email" substring matches before "match" does);
  // anything unrecognized keeps the translated generic fallback. String
  // comparison, not the reason-code type: the classifier's return values
  // predate and don't match the analytics schema union.
  const errorTextReasonCode: string | undefined = errorText
    ? getAuthProtectedIdentifierVerificationFailureReasonCode(errorText)
    : undefined;
  const isUserCorrectableEmailError =
    errorTextReasonCode === 'invalid_email' ||
    errorTextReasonCode === 'verification_failed';
  const bannerTitle = isUserCorrectableEmailError
    ? t('Invalid email')
    : t("Couldn't send your code");
  const bannerMessage =
    errorText && isUserCorrectableEmailError
      ? errorText
      : t('Try again in a moment');
  const formatCountdown = (totalSeconds: number) =>
    `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(
      2,
      '0'
    )}`;
  const cooldownMessage = t('Try again in {{countdown}}', {
    countdown: formatCountdown(remainingSeconds),
  });
  // Announced once per rate-limit response; the ticking countdown is hidden
  // from assistive tech so the assertive live region doesn't re-announce
  // every second. Spelled-out, rounded-up minutes: a one-shot "four
  // fifty-nine" reads as a clock time, and whole minutes avoid plural forms
  // beyond the 1/other split every current locale uses.
  const snapshotSeconds = Math.max(0, Math.ceil(retryAfterSeconds));
  const snapshotMinutes = Math.ceil(snapshotSeconds / 60);
  const cooldownAnnouncement =
    snapshotSeconds < 60
      ? t('Try again in less than a minute')
      : snapshotMinutes === 1
        ? t('Try again in 1 minute')
        : t('Try again in {{minutes}} minutes', { minutes: snapshotMinutes });
  const localizedValidationError = translateValidationError(t, validationError);
  const subHeading = maskedEmail
    ? t(
        "Enter the email address that matches {{maskedEmail}}. If it matches our records, we'll send your login code.",
        { maskedEmail }
      )
    : t(
        "Enter the email address linked to this username. If it matches our records, we'll send your login code."
      );

  return (
    <Layout
      heading={t("Let's confirm it's you")}
      subHeading={subHeading}
      subHeadingClasses="mx-auto max-w-[420px]"
      variant="banner"
      bannerImageDesktop={bannerLoginImage}
    >
      <div className="space-y-6">
        {isCooldownActive ? (
          <Alert
            role="alert"
            status="error"
            variant="page-level"
            icon={<InfoIcon />}
            title={t('Too many attempts')}
            message={
              <>
                <span aria-hidden="true">{cooldownMessage}</span>
                <span className="sr-only">{cooldownAnnouncement}</span>
              </>
            }
          />
        ) : showSubmissionErrorBanner ? (
          <Alert
            role="alert"
            status="error"
            variant="page-level"
            icon={<InfoIcon />}
            title={bannerTitle}
            message={bannerMessage}
          />
        ) : null}

        <AuthForm>
          <InputText
            ref={emailInputRef}
            label={t('Email')}
            name="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            required
            autoComplete="email"
            errorMessage={localizedValidationError}
            state={validationError ? 'error' : 'default'}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter' && !isConfirming && !isCooldownActive) {
                event.preventDefault();
                void handleConfirm();
              }
            }}
          />
          {/* Always-mounted status region: announces the inline validation
              error for Enter-key submissions, where focus never moves and an
              aria-describedby change on the focused field isn't read. */}
          <span role="status" className="sr-only">
            {localizedValidationError}
          </span>
          <Button
            onClick={handleConfirm}
            label={t('Confirm')}
            state={isConfirming ? 'loading' : 'default'}
            disabled={isCooldownActive}
            variant="primary"
          />
        </AuthForm>

        <div className="text-center">
          <AuthLink>
            <button type="button" onClick={onClickUseDifferentLogin}>
              {t('Use a different email or username')}
            </button>
          </AuthLink>
        </div>
      </div>
    </Layout>
  );
};

export default ProtectedEmailConfirmationScreen;
